<template>
  <div>
    <page-header
      :title="$t('company_limits_title')"
      :subtitle="$t('company_limits_subtitle')"
      icon="mdi-shield-account"
      icon-color="primary"
    >
      <template #actions>
        <primary-button @click="openLimitWizard">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_new_limit") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar :searchable="false">
      <template #filters>
        <v-col cols="auto">
          <text-field
            v-model="search"
            :label="$t('search_limits')"
            kind="search"
            hide-details
            color="white"
          />
        </v-col>
      </template>
    </page-toolbar>

    <div class="px-6 mt-2">
      <v-data-table
        :headers="headers"
        :items="filteredLimits"
        :loading="isLoading"
        :loading-text="$t('loading')"
        class="ordera-box-shadow striped-table"
        mobile-breakpoint="0"
      >
        <template v-slot:[`item.name`]="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.name }}</div>
            <div
              class="text-caption text--secondary"
              v-if="item.params.metadata?.description"
            >
              {{ item.params.metadata.description }}
            </div>
          </div>
        </template>

        <template v-slot:[`item.params.limit_definition.active`]="{ item }">
          <div class="text-body-2">
            {{ getLimitTypeLabel(item.params.limit_definition?.active) }}
          </div>
        </template>

        <template v-slot:[`item.params.timing.active`]="{ item }">
          <v-chip small outlined>
            {{ getTimeLimitLabel(item.params.timing?.active) }}
          </v-chip>
        </template>

        <template v-slot:[`item.params.payment_logic.active`]="{ item }">
          <div class="text-body-2">
            {{ getWhoPaysLabel(item.params.payment_logic?.active) }}
          </div>
        </template>

        <template v-slot:[`item.params.ordering_rules.active`]="{ item }">
          <div class="text-body-2">
            {{ getWhatCanOrderLabel(item.params.ordering_rules?.active) }}
          </div>
        </template>

        <template v-slot:item.active="{ item }">
          <v-switch
            v-model="item.active"
            dense
            hide-details
            @change="toggleLimitActive(item)"
          />
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end" style="gap: 4px">
            <circle-button
              icon="mdi-pencil"
              @click="editLimit(item)"
              :tooltip="$t('edit')"
              small
            />
            <circle-button
              icon="mdi-delete"
              @click="deleteLimit(item)"
              color="error"
              :tooltip="$t('delete')"
              small
            />
          </div>
        </template>

        <template v-slot:no-data>
          <empty-state
            icon="mdi-shield-off"
            :title="$t('no_limits_defined')"
            :subtitle="$t('no_limits_subtitle')"
            class="ma-4"
          >
            <primary-button @click="openLimitWizard">
              <v-icon left>mdi-plus-circle</v-icon>
              {{ $t("add_new_limit") }}
            </primary-button>
          </empty-state>
        </template>
      </v-data-table>
    </div>

    <create-limit-dialog
      v-model="createDialog"
      :company-id="companyId"
      @created="handleLimitCreated"
    />

    <limit-configuration-dialog
      v-if="editingLimit"
      v-model="configDialog"
      :limit="editingLimit"
      :company-id="companyId"
      @saved="loadLimits"
    />
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import EmptyState from "@/components/global/EmptyState.vue";
import limitService from "@/services/limit";
import CreateLimitDialog from "@/components/limitConfiguration/dialogs/CreateLimitDialog.vue";
import LimitConfigurationDialog from "@/components/limitConfiguration/dialogs/LimitConfigurationDialog.vue";
import {
  getLimitTypeLabelKey,
  getTimeLimitLabelKey,
  getWhoPaysLabelKey,
  getWhatCanOrderLabelKey,
  normalizeLimitData,
} from "@/utils/limitHelpers";

export default {
  name: "CompanyLimits",
  mixins: [hasSnackbar],
  components: {
    EmptyState,
    CreateLimitDialog,
    LimitConfigurationDialog,
  },
  data() {
    return {
      isLoading: false,
      search: "",
      limits: [],
      createDialog: false,
      configDialog: false,
      editingLimit: null,
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id;
    },
    filteredLimits() {
      if (!this.$t || !this.limits) return this.limits || [];
      if (!this.customSearch) return this.limits || [];
      return this.customSearch(this.limits, this.headers, this.search);
    },
    headers() {
      if (!this.$t) return [];
      return [
        {
          text: this.$t("name"),
          value: "name",
          sortable: true,
        },
        {
          text: this.$t("limit_type"),
          value: "params.limit_definition.active",
          sortable: true,
        },
        {
          text: this.$t("time_limit"),
          value: "params.timing.active",
          sortable: true,
        },
        {
          text: this.$t("who_pays"),
          value: "params.payment_logic.active",
          sortable: true,
        },
        {
          text: this.$t("what_can_order"),
          value: "params.ordering_rules.active",
          sortable: true,
        },
        {
          text: this.$t("active"),
          value: "active",
          align: "center",
          sortable: true,
        },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          sortable: false,
        },
      ];
    },
  },
  mounted() {
    this.loadLimits();
  },
  methods: {
    async loadLimits() {
      this.isLoading = true;
      try {
        const { data } = await limitService.getCompanyLimits();

        // Primeni default vrednosti na svaki limit iz API-ja
        this.limits = (data?.data || []).map((limit) => normalizeLimitData(limit));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_limits"));
      } finally {
        this.isLoading = false;
      }
    },
    openLimitWizard() {
      this.createDialog = true;
    },
    async handleLimitCreated(limit) {
      await this.loadLimits();
      // Open config dialog to edit the newly created limit
      this.editingLimit = limit;
      this.configDialog = true;
    },
    editLimit(item) {
      this.editingLimit = item;
      this.configDialog = true;
    },
    async deleteLimit(item) {
      const confirmed = await this.$confirm(this.$t("confirm_delete_limit"));
      if (!confirmed) return;

      try {
        await limitService.deleteLimit(item.id);
        this.showSuccessSnackbar(this.$t("limit_deleted_successfully"));
        this.loadLimits();
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_deleting_limit"));
      }
    },
    async toggleLimitActive(item) {
      try {
        await limitService.toggleLimitActive(item.id, item.active);
        this.showSuccessSnackbar(
          item.active ? this.$t("limit_activated") : this.$t("limit_deactivated")
        );
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_changing_limit_status"));
        item.active = !item.active;
      }
    },
    getLimitTypeLabel(type) {
      const key = getLimitTypeLabelKey(type);
      return key ? this.$t(key) : type || "";
    },
    getTimeLimitLabel(type) {
      const key = getTimeLimitLabelKey(type);
      return key ? this.$t(key) : type || "";
    },
    getWhoPaysLabel(type) {
      const key = getWhoPaysLabelKey(type);
      return key ? this.$t(key) : type || "";
    },
    getWhatCanOrderLabel(type) {
      if (Array.isArray(type)) {
        if (type.length === 0) return "";
        return type
          .map((t) => {
            const key = getWhatCanOrderLabelKey(t);
            return key ? this.$t(key) : "";
          })
          .filter(Boolean)
          .join(", ");
      }
      const key = getWhatCanOrderLabelKey(type);
      return key ? this.$t(key) : type || "";
    },
  },
};
</script>
